import { RefObject, useEffect } from 'react';

export const STICKY_COLUMN_GAP_PX = 24;

/** Nearest ancestor with non-visible overflow: what `position: sticky` resolves against. */
const findStickyScroller = (element: HTMLElement): HTMLElement | null => {
    for (let node = element.parentElement; node; node = node.parentElement) {
        const { overflowY } = window.getComputedStyle(node);
        if (overflowY !== 'visible') return node;
    }
    return null;
};

/** Sets a sticky column's `top` so it scrolls until its bottom is in view, then pins. */
export const useStickyColumn = (ref: RefObject<HTMLElement>, enabled: boolean = true) => {
    useEffect(() => {
        const column = ref.current;
        if (!column) return undefined;
        if (!enabled) {
            column.style.removeProperty('top');
            return undefined;
        }

        const scroller = findStickyScroller(column);
        // Offsets are measured inside the scroller's padding, so only the window needs the gap.
        const update = () => {
            if (!scroller) {
                column.style.top = `${Math.min(STICKY_COLUMN_GAP_PX, window.innerHeight - column.offsetHeight - STICKY_COLUMN_GAP_PX)}px`;
                return;
            }
            const { paddingTop, paddingBottom } = window.getComputedStyle(scroller);
            const stickyViewHeight = scroller.clientHeight - parseFloat(paddingTop) - parseFloat(paddingBottom);
            column.style.top = `${Math.min(0, stickyViewHeight - column.offsetHeight)}px`;
        };

        update();
        const observer = new ResizeObserver(update);
        observer.observe(column);
        if (scroller) observer.observe(scroller);
        window.addEventListener('resize', update);

        return () => {
            observer.disconnect();
            window.removeEventListener('resize', update);
            column.style.removeProperty('top');
        };
    }, [ref, enabled]);
};
